// @ts-nocheck
import { memo, useEffect, useRef, useState } from "react";
import {
  Button,
  FormControl,
  MenuItem,
  Select,
  Box,
  TextField,
  Radio,
  ListItemText,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { AutoSizer, List } from "react-virtualized";
import "./multiselect.scss";

interface SingleSelectWithoutClearButtonProps {
  options: string[];
  label?: string;
  selectedValues: string[];
  onApply: (selected: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MAX_MENU_HEIGHT = ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP;
const MENU_WIDTH = 250;

const SingleSelectWithoutClearButton: React.FC<SingleSelectWithoutClearButtonProps> = ({
  options,
  label,
  selectedValues,
  onApply,
  disabled,
  placeholder,
}) => {
  const [tempSelectedOption, setTempSelectedOption] = useState<string | null>(selectedValues[0] || null);
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTempSelectedOption(selectedValues[0] || null);
  }, [selectedValues]);

  const handleSelectChange = (option: string) => {
    setTempSelectedOption(option); // Update to only one selected option
  };

  const handleApply = () => {
    if (tempSelectedOption) {
      onApply([tempSelectedOption]); // Pass an array with only the selected value
    }
    setOpen(false);
    setSearchQuery(""); // Reset search query on apply
  };

  const handleClose = () => {
    setOpen(false);
    setSearchQuery(""); // Reset search query on close
  };

  const handleOpen = () => {
    setOpen(true);
    setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
  };

  const handleFieldMouseDown = (event: React.MouseEvent) => {
    if (disabled || open) return;
    const target = event.target as HTMLElement;
    if (target.closest('.MuiMenu-paper, .MuiPopover-paper')) return;
    handleOpen();
  };

  const handleInputClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent select from closing when clicking inside the search box
  };

  const filteredOptions = options.filter(option =>
    option.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const rowRenderer = ({ index, key, style }: { index: number; key: string; style: React.CSSProperties }) => {
    const option = filteredOptions[index];
    return (
      <MenuItem
        key={key}
        value={option}
        style={style}
        disableRipple
        onClick={() => handleSelectChange(option)}
      >
        <ListItemText primary={option} />
        <Radio checked={tempSelectedOption === option} /> {/* Single selection: check if option matches selected */}
      </MenuItem>
    );
  };

  return (
    <FormControl fullWidth className="single-select-wrapper-without-clear">
      {label && <label id="multi-select-label" className={`${disabled ? "disabled" : ""}`}>{label}</label>}
      <Select
        labelId="multi-select-label"
        value={tempSelectedOption || ""} // Single selection, just pass the string value
        IconComponent={ExpandMore}
        onClose={handleClose}
        open={open}
        onOpen={handleOpen}
        onMouseDown={handleFieldMouseDown}
        disabled={disabled}
        placeholder={placeholder || "Search..."}
        renderValue={(selected) => selected || placeholder} 
        className="multi-select-without-clear"
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: MAX_MENU_HEIGHT,
              width: MENU_WIDTH,
            },
            className: "multi-select-wrapper-without-clear",
          },
        }}
      >
        <MenuItem>
          <TextField
            inputRef={inputRef} // Reference to the input
            placeholder={placeholder || "Search..."}
            fullWidth
            onChange={(e) => setSearchQuery(e.target.value)}
            value={searchQuery}
            onClick={handleInputClick} // Prevent closing on click
          />
        </MenuItem>
        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <List
              width={width}
              height={Math.min(ITEM_HEIGHT * 4.5, 300)}
              rowCount={filteredOptions.length}
              rowHeight={ITEM_HEIGHT}
              rowRenderer={rowRenderer}
              overscanRowCount={5}
            />
          )}
        </AutoSizer>

        <Box className="flex justify-content-center align-items-center list-footer">
          <Button onClick={handleApply} className="primary-medium-green-btn" disableRipple>
            Done
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};

export default memo(SingleSelectWithoutClearButton);
